import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  toggleCart,
  toggleWishlistDrawer,
  removeFromCart,
  toggleWishlist,
  addToCart,
  clearCart
} from '../../redux/slice/user.slice';
import { X, ShoppingBag, Heart, Trash2, ArrowRight, ShoppingCart, CreditCard, Sparkles } from 'lucide-react';
import gsap from 'gsap';
import { useNavigate } from 'react-router-dom';

export default function CartWishlistDrawer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { games } = useSelector((s) => s.game);
  const { cart, wishlist, isCartOpen, isWishlistOpen, wishlistDetailed = [] } = useSelector((s) => s.userSlice);

  const cartTotal = cart.reduce((total, item) => {
    const plat = item.platform || 'windows';
    const price = item.price !== undefined ? item.price : (item.game?.platforms?.[plat]?.price !== undefined
      ? item.game.platforms[plat].price
      : (item.game?.price || 0));
    return total + price * item.quantity;
  }, 0);

  const drawerRef = useRef(null);
  const backdropRef = useRef(null);

  // Sync opening animations
  const isOpen = isCartOpen || isWishlistOpen;

  useEffect(() => {
    if (isOpen) {
      const ctx = gsap.context(() => {
        gsap.fromTo(backdropRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.3 }
        );
        gsap.fromTo(drawerRef.current,
          { x: '100%' },
          { x: '0%', duration: 0.5, ease: "power4.out" }
        );
      });
      return () => ctx.revert();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Smooth close action
  const handleClose = () => {
    gsap.to(drawerRef.current, {
      x: '100%',
      duration: 0.4,
      ease: "power2.in",
      onComplete: () => {
        if (isCartOpen) dispatch(toggleCart());
        if (isWishlistOpen) dispatch(toggleWishlistDrawer());
      }
    });
    gsap.to(backdropRef.current, {
      opacity: 0,
      duration: 0.4
    });
  };

  const handleCheckout = () => {
    //alert("🌌 CHECKOUT VERIFIED!\n\nYour purchase of " + cart.length + " games has been verified in the local decentralized retinal database. License signatures are loading into your library. Keep hacking!");
    navigate('/checkout');
    handleClose();
  };

  const moveWishlistToCart = (game) => {
    dispatch(addToCart(game));
    dispatch(toggleWishlist(game.id)); // Remove from wishlist once added to cart
  };

  // Filter games inside wishlist
  const wishlistGames = wishlistDetailed.length > 0
    ? wishlistDetailed
    : games.filter(g => wishlist.includes(g.id || g._id));

  return (
    <div className="fixed inset-0 z-50 overflow-hidden transform-gpu">
      {/* Drawer Background Blur Overlay */}
      <div
        ref={backdropRef}
        onClick={handleClose}
        className="fixed inset-0 bg-cyber-black/75 backdrop-blur-sm transition-opacity duration-300"
      ></div>

      {/* Slide-out Panel Structure */}
      <div
        ref={drawerRef}
        className="fixed inset-y-0 right-0 max-w-md w-full glass-panel-glow border-l border-cyber-blue/20 shadow-[-10px_0_30px_rgba(0,0,0,0.8)] flex flex-col justify-between z-10 translate-x-full transform-gpu h-full"
      >
        {/* running vertical strip edge glow line */}
        <div className="absolute left-0 top-0 h-full w-[2px] bg-gradient-to-b from-cyber-blue via-cyber-purple to-cyber-pink"></div>

        {/* Drawer Header */}
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isCartOpen ? (
              <>
                <ShoppingBag className="w-5 h-5 text-cyber-blue animate-pulse" />
                <span className="font-cyber font-semibold text-lg text-white uppercase tracking-wider">YOUR CART</span>
              </>
            ) : (
              <>
                <Heart className="w-5 h-5 text-cyber-pink animate-pulse" />
                <span className="font-cyber font-semibold text-lg text-white uppercase tracking-wider">YOUR WISHLIST</span>
              </>
            )}
            <span className="text-xs text-gray-500 font-cyber">
              {isCartOpen ? `[${cart.length}]` : `[${wishlist.length}]`}
            </span>
          </div>

          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10 text-gray-400 hover:text-white transition-all"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Body (Scrollable items container) */}
        <div data-lenis-prevent className="flex-1 overflow-y-auto p-6 space-y-6">


          {/* CART VIEW */}
          {isCartOpen && (
            cart.length > 0 ? (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div
                    key={`${item.game.id}-${item.platform || 'windows'}`}
                    className="flex items-center gap-4 p-3 rounded-xl border border-white/5 bg-white/5 relative group hover:border-cyber-blue/30 transition-all duration-300"
                  >
                    {/* Game cover thumbnail */}
                    <img
                      src={item.game.cover_image?.url || item.game.image}
                      alt={item.game.title}
                      className="w-16 h-16 rounded-lg object-cover border border-white/10"
                    />

                    {/* Meta and details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-cyber font-semibold text-sm text-white truncate">{item.game.title}</h4>
                      <p className="text-xs text-cyber-cyan font-cyber font-medium mt-0.5">
                        {item.game.category?.name} <span className="text-gray-500 font-mono text-[9px] uppercase ml-1 bg-white/5 px-1 rounded border border-white/5">{(item.platform || 'windows')}</span>
                      </p>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-cyber font-semibold text-white">
                          ${(() => {
                            const plat = item?.platform || 'windows';
                            const price = item?.price !== undefined ? item?.price : (item?.game?.platforms?.[plat]?.price !== undefined
                              ? item?.game?.platforms?.[plat]?.price
                              : (item?.game?.price || 0));
                            return price.toFixed(2);
                          })()}
                        </span>
                        {(item?.discount > 0 || item?.game?.discount > 0) && (
                          <div className="flex items-center gap-1">
                            <span className="text-[13px] text-gray-500 line-through font-mono">
                              ${(() => {
                                const plat = item?.platform || 'windows';
                                const originalPrice = item?.originalPrice !== undefined ? item?.originalPrice : (item?.game?.platforms?.[plat]?.originalPrice !== undefined
                                  ? item?.game?.platforms?.[plat]?.originalPrice
                                  : (item?.game?.originalPrice || 0));
                                return originalPrice.toFixed(2);
                              })()}
                            </span>
                            <span className="text-cyber-green text-[9px] font-cyber font-bold">-{(item?.discount !== undefined ? item?.discount : item?.game?.discount)}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delete Button */}
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => dispatch(removeFromCart({ gameId: item.game._id || item.game.id, platform: item.platform || 'windows' }))}
                        className="text-gray-500 hover:text-cyber-pink p-1 transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty Cart Screen */
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-20">
                <div className="w-12 h-12 rounded-full border border-dashed border-cyber-blue/30 flex items-center justify-center text-cyber-blue animate-pulse">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-cyber text-white font-medium text-sm uppercase">Your cart is empty</h4>
                  <p className="text-xs text-gray-500 max-w-xs mt-1 leading-relaxed">
                    You have not added any games yet. Browse our store to find something you like.
                  </p>
                </div>
              </div>
            )
          )}

          {/* WISHLIST VIEW */}
          {isWishlistOpen && (
            wishlistGames.length > 0 ? (
              <div className="space-y-4">
                {wishlistGames.map((game) => (
                  <div
                    key={game.id}
                    className="flex items-center gap-4 p-3 rounded-xl border border-white/5 bg-white/5 hover:border-cyber-pink/30 transition-all duration-300"
                  >
                    {/* Game cover thumbnail */}
                    <img
                      src={game.cover_image?.url || game.image}
                      alt={game.title}
                      className="w-16 h-16 rounded-lg object-cover border border-white/10"
                    />

                    {/* Meta and details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-cyber font-semibold text-sm text-white truncate">{game.title}</h4>
                      <p className="text-xs text-cyber-pink font-cyber font-medium mt-0.5">{game.category?.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-cyber font-semibold text-white">
                          ${(game.price * (1 - (game.discount || 0) / 100)).toFixed(2)}
                        </span>
                        {game.discount > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-[11px] text-gray-500 line-through font-mono">
                              ${game.price.toFixed(2)}
                            </span>
                            <span className="text-cyber-green text-[9px] font-cyber font-bold">-{game.discount}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => moveWishlistToCart(game)}
                        className="cyber-btn !p-2"
                        title="Acquire License / Add to Cart"
                      >
                        <ShoppingCart className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => dispatch(toggleWishlist(game.id))}
                        className="p-2 rounded-lg bg-white/5 border border-white/10 hover:border-cyber-pink/40 text-gray-400 hover:text-cyber-pink transition-all"
                        title="Ditch Wishlist"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty Wishlist Screen */
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-20">
                <div className="w-12 h-12 rounded-full border border-dashed border-cyber-pink/30 flex items-center justify-center text-cyber-pink animate-pulse">
                  <Heart className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-cyber text-white font-medium text-sm uppercase">Your wishlist is empty</h4>
                  <p className="text-xs text-gray-500 max-w-xs mt-1 leading-relaxed">
                    You have not saved any games yet. Browse our store to add your favourites.
                  </p>
                </div>
              </div>
            )
          )}

        </div>

        {/* Drawer Footer (Checkout Block) */}
        {isCartOpen && cart.length > 0 && (
          <div className="p-6 border-t border-white/5 bg-cyber-black/80 space-y-4">

            {/* Invoice Breakdown */}
            <div className="space-y-1.5 text-sm font-sans">
              <div className="flex justify-between text-gray-400">
                <span>Transaction Fees</span>
                <span className="text-cyber-green font-mono uppercase">Free</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span className="text-white">${cartTotal.toFixed(2)}</span>
              </div>
              <div className="h-[1px] bg-white/10 my-2"></div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm font-cyber font-medium text-white uppercase">TOTAL PRICE</span>
                <span className="text-2xl font-cyber font-semibold text-cyber-blue text-glow-cyan">${cartTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Check Out CTA */}
            <button
              onClick={handleCheckout}
              className="cyber-btn w-full !py-4 flex items-center justify-center space-x-2"
            >
              <CreditCard className="w-4 h-4 text-inherit" />
              <span>PROCEED TO CHECKOUT</span>
            </button>
            <p className="text-[10px] text-gray-500 text-center font-mono uppercase">
              100% safe and secure payment
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
